"use client";

import {
  useState,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useMemo,
  type KeyboardEvent,
  type ClipboardEvent,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { extractMentionQuery } from "./MentionDropdown";
import { MentionDropdown, type MentionSelection } from "./MentionDropdown";
import { EmojiSuggestionDropdown, type EmojiSuggestion } from "./EmojiSuggestionDropdown";
import { useUserSearch } from "@/lib/hooks/useUserSearch";
import { recordSubmitShortcutUse } from "@/lib/hooks/useSubmitShortcut";
import { getCustomEmojis } from "@/lib/api/emojis";
import { searchStandardEmojis } from "@/lib/standard-emojis";
import {
  getTextRepresentation,
  getCaretOffsetInText,
  setCaretOffsetInText,
} from "@/lib/contentEditable";
import type { CustomEmoji } from "@/lib/api/emojis";
import {
  insertBrAtCaret,
  isRangeFullyWrapped,
  mentionEveryoneFragment,
  mentionFragment,
  renderTextIntoEditor,
  replaceTextRangeWithFragment,
  serializeEditorToRaw,
  toggleInlineTag,
  applyLeadingHighlight,
} from "@/lib/richText/editorDom";
import type { InlineTag } from "@/lib/richText/editorDom";
import { customEmojisKey, useV1Group } from "@/lib/hooks/useGroupPage";
import { isShortcodePrefix } from "@/lib/emoji/shortcode";
import { scrollCaretIntoView } from "@/lib/caretInView";

export interface RichTextInputHandle {
  /** Return the text with @DisplayName mentions converted to `@[id]` and inline emoji imgs to :shortcode:. */
  getRaw: () => string;
  /** Clear internal state — called after a successful submit. */
  clearMentions: () => void;
  /** Focus the input. */
  focus: () => void;
  /** Take focus off the input, leaving its text alone. */
  blur: () => void;
  /**
   * Open an EMPTY editor with one mention chip and a trailing space, caret
   * after it — what pressing Svar does (OSO-596).
   *
   * A REAL CHIP, not the text `@[id]`: the same `span[data-user-id]` the
   * autocomplete builds, so `getRaw()` serializes it to `@[id]` and the
   * reader sees a name. Does nothing if there is already text in the editor,
   * so a restored draft is never overwritten.
   *
   * IT DOES NOT EMIT `onChange`. The suggestion is in the editor and nowhere
   * else until the author types — so it is never saved as a draft, and the box
   * still counts as empty for whoever is deciding whether there is anything to
   * send. See the implementation for what went wrong when it did emit.
   */
  prefillMention: (mention: { userId: number; displayName: string }) => void;
}

interface RichTextInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * THE FIELD'S ACCESSIBLE NAME, and it has to be a prop because the visible
   * placeholder cannot supply one: it is drawn by CSS
   * (`.theta-rich-text-input::before { content: attr(data-placeholder) }`), so
   * it is decoration, not text, and a `contenteditable` is not a plain text
   * field whose `placeholder` a screen reader would fall back to anyway. Without it
   * axe counted 29 unnamed textboxes — 20 on /feed alone, one per post's
   * comment box (OSO-640).
   *
   * Every caller passes one. Optional only so the two dozen render-prop tests
   * that construct the component directly do not have to.
   */
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
  /** Announce the field as multiline to assistive tech. Enter always inserts a newline regardless. */
  multiline?: boolean;
  /** Min height for the editable area in px. */
  minHeight?: number;
  /** Reserved — contenteditable always autosizes. */
  autoResize?: boolean;
  autoFocus?: boolean;
  /** Called when Cmd/Ctrl+Enter is pressed. Plain Enter never submits. */
  onSubmit?: () => void;
  /** Called when Escape is pressed (and no autocomplete is open). */
  onEscape?: () => void;
  /** Group context for custom emoji autocomplete and @-mention suggestions (optional). */
  groupId?: number;
  /**
   * The same group's SLUG, when the caller has it. Only used to key the
   * `can.moderate` lookup behind the `@alle` suggestion (OSO-681) on the SAME
   * cache entry the group pages already hold (`useV1Group` keys by slug), so
   * a composer on a group page adds no second fetch and cannot disagree with
   * the page. Without it the lookup falls back to an id-keyed fetch — the
   * event surfaces have only `discussionGroupId` and no slug-keyed twin
   * exists there to duplicate.
   */
  groupSlug?: string;
  /**
   * Suppress the `@alle` suggestion because the write path would not honour
   * the chip: pass it while composing ANONYMOUSLY (the server refuses @alle
   * on anonymous posts — the chip would prove the author moderates) and from
   * EDIT composers (edits produce no notifications, so the row's promise
   * would be false). Personal mentions are unaffected.
   */
  hideMentionAll?: boolean;
  /** Called when files (e.g. pasted images) are in the clipboard. When provided and files are present, the paste is consumed for file upload instead of inserting text. */
  onPasteFiles?: (files: File[]) => void;
  /** Controls whether the bold/italic/underline row is shown. Omit to let the component manage this itself (and render its own "Aa" toggle icon above the editor). Pass this (with onFormattingOpenChange) when a parent composer renders its own trigger elsewhere — e.g. alongside its other icons in a bottom toolbar — via hideFormattingIcon. */
  formattingOpen?: boolean;
  onFormattingOpenChange?: (open: boolean) => void;
  /** Suppress the built-in "Aa" toggle icon. The bold/italic/underline row still renders above the editor when open — either because a parent drives formattingOpen from its own trigger, or because a formatting shortcut revealed it (OSO-647); a shortcut-revealed row closes again when the parent clears the value after submit. */
  hideFormattingIcon?: boolean;
  /**
   * Light up this many characters at the start of the text (after leading
   * whitespace) — the DM composer's "Kære <navn>" (lib/botAddress.ts). They
   * are wrapped in a styled span (`applyLeadingHighlight`), re-checked after
   * every change and never touched while an IME composition is in flight:
   * moving DOM under a composing keyboard cancels the composition, and a
   * Danish name on a phone keyboard is composed one autocorrect at a time.
   */
  highlightLeading?: number | null;
}

/**
 * THE PLACEHOLDER IS DRAWN OFF THE EDITOR, NOT OFF `value` (OSO-670).
 *
 * `[data-empty="true"]::before` (globals.css) is the placeholder, and `value`
 * is not a safe proxy for "the editor is empty": `prefillMention` puts a chip
 * in it and deliberately does not emit, so `value` sits at `""` with a chip on
 * screen. Once the two have diverged React stops being able to correct it —
 * the prop it would write is unchanged, so backspacing that chip away left an
 * empty box flagged non-empty and the placeholder never came back.
 *
 * So every path that changes the editor's content ends here. `text` is the
 * same text representation `onChange` emits, which keeps this exactly the
 * emptiness test the render path makes from `value`.
 */
function setEmptyFlag(el: HTMLElement, text: string): void {
  el.dataset.empty = text === "" ? "true" : "false";
}

/** Detect a `:shortcode` token at cursor. Returns null if not in one. */
function extractEmojiQuery(text: string, cursorPos: number): { query: string; startIndex: number } | null {
  for (let i = cursorPos - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === ":" && (i === 0 || /\s/.test(text[i - 1]))) {
      const query = text.slice(i + 1, cursorPos);
      if (!isShortcodePrefix(query)) return null;
      return { query, startIndex: i };
    }
    if (/\s/.test(text[i])) return null;
    if (cursorPos - i > 32) return null;
  }
  return null;
}

export const RichTextInput = forwardRef<RichTextInputHandle, RichTextInputProps>(
  function RichTextInput(
    {
      value,
      onChange,
      placeholder,
      ariaLabel,
      disabled,
      className,
      multiline,
      minHeight,
      autoFocus,
      onSubmit,
      onEscape,
      groupId,
      groupSlug,
      hideMentionAll,
      onPasteFiles,
      formattingOpen: formattingOpenProp,
      onFormattingOpenChange,
      hideFormattingIcon,
      highlightLeading,
    },
    ref
  ) {
    const editorRef = useRef<HTMLDivElement>(null);
    /** The last text representation we computed and emitted via onChange — used to detect external value changes. */
    const lastEmitted = useRef<string>("");

    /**
     * HAS ANYBODY TOUCHED THIS BOX YET?
     *
     * The two lists below — the group's members, for `@`, and its custom
     * emoji, for `:` — are per GROUP, not per composer, so react-query fetches
     * each once however many cards share it. That was still two requests per
     * group ON MOUNT, and a feed page carries a composer under every card:
     * measured against production, nine groups on the first page meant
     * eighteen requests of 266–454 ms each, fired ~1.8 s into the load, for
     * suggestion lists nobody had asked to see. They competed with the
     * avatars and attachments that make the feed look finished.
     *
     * FOCUS IS THE SIGNAL, not the first `@` or `:`. Waiting for the trigger
     * character would put a network round trip between the keystroke and the
     * dropdown; waiting for focus does the fetch while the author is still
     * deciding what to type, and a composer that is never focused — which on
     * a feed is nearly all of them — costs nothing at all.
     *
     * It is sticky: once fetched, the lists stay in the query cache under
     * their own keys and blurring does not throw them away.
     */
    const [engaged, setEngaged] = useState(false);

    // Mention state
    const [mentionQuery, setMentionQuery] = useState("");
    const [mentionStart, setMentionStart] = useState(-1);
    const [mentionIndex, setMentionIndex] = useState(0);
    const prevMentionQuery = useRef("");
    const mentionUsers = useUserSearch(mentionQuery, groupId, engaged);

    /**
     * The group document, for ONE bit: `can.moderate` gates the `@alle`
     * suggestion (OSO-681) — admins/moderators only, the same boolean the
     * server enforces at write time (`composePost`), so the dropdown never
     * offers what the write would refuse. `useV1Group` — the SAME hook the
     * group pages use, so one cache key has one behaviour (retry included) —
     * gated on `engaged` like the member list and the emoji above by handing
     * it an empty ref until somebody focuses. Keyed by SLUG when the caller
     * has one — the exact entry a group page already holds, so no second
     * fetch and no two answers; the id-keyed fallback exists for the event
     * surfaces, which know only `discussionGroupId` and load no slug-keyed
     * twin.
     */
    const groupRef = groupSlug ?? (groupId != null ? String(groupId) : "");
    const { data: mentionGroup } = useV1Group(engaged ? groupRef : "");

    /**
     * WHERE `@alle` RANKS — never the default Enter target of a partial name
     * (review of PR #386): with it first at query "a", a moderator tagging
     * "@a<Enter>" for Anna would blast the whole group. So it leads ONLY when
     * the query says exactly "alle"; for a prefix it trails the people, still
     * reachable by arrow keys but never by accident — and for a prefix with
     * NO people (the search still debouncing, or nobody matching) it is not
     * offered at all, because the only row in a list IS the Enter default,
     * whatever it is ranked.
     *
     * `hideMentionAll` is the caller saying the write path would not honour
     * the chip here — composing anonymously (the server refuses @alle on
     * anonymous posts), or an EDIT composer (edits produce no notifications,
     * so the row's "giv alle besked" would be a false promise).
     */
    const mentionQ = mentionQuery.toLowerCase();
    const mentionAllRank: "first" | "last" | undefined =
      !hideMentionAll &&
      mentionGroup?.can?.moderate === true &&
      mentionQ.length > 0 &&
      "alle".startsWith(mentionQ)
        ? mentionQ === "alle"
          ? "first"
          : mentionUsers.length > 0
            ? "last"
            : undefined
        : undefined;

    /**
     * The dropdown's rows, in the order it draws them. One list, so the
     * keyboard's index and the pointer's row can never disagree about which
     * entry is which.
     */
    const mentionRows: MentionSelection[] = useMemo(() => {
      const people = mentionUsers.map((u) => ({
        kind: "user" as const,
        userId: u.id,
        displayName: u.name ?? "",
      }));
      if (mentionAllRank === "first") return [{ kind: "all" as const }, ...people];
      if (mentionAllRank === "last") return [...people, { kind: "all" as const }];
      return people;
    }, [mentionAllRank, mentionUsers]);

    // Emoji state
    const [emojiQuery, setEmojiQuery] = useState<string | null>(null);
    const [emojiStart, setEmojiStart] = useState(-1);
    const [emojiIndex, setEmojiIndex] = useState(0);
    const prevEmojiQuery = useRef<string | null>(null);

    // Formatting toolbar state — controlled by the parent when it passes
    // formattingOpen (e.g. driving it from its own "Aa" button elsewhere),
    // otherwise managed internally so the built-in icon works standalone.
    const [internalToolbarOpen, setInternalToolbarOpen] = useState(false);
    const toolbarOpen = formattingOpenProp ?? internalToolbarOpen;
    const setToolbarOpen = useCallback(
      (next: boolean | ((prev: boolean) => boolean)) => {
        // Resolved OUTSIDE the updater: an updater must be pure, and this one
        // used to call the parent's setState from inside it — StrictMode runs
        // updaters more than once. The parent hears only actual changes, so a
        // shortcut held down doesn't re-notify it on every repeat.
        const resolved = typeof next === "function" ? next(toolbarOpen) : next;
        if (resolved !== toolbarOpen) onFormattingOpenChange?.(resolved);
        setInternalToolbarOpen(resolved);
      },
      [toolbarOpen, onFormattingOpenChange]
    );
    const [activeFormats, setActiveFormats] = useState<Set<InlineTag>>(new Set());

    // Lazy-load custom emojis once per group, from the first focus — see
    // `engaged` above for why that is the trigger and not mount.
    const { data: emojiData } = useQuery({
      queryKey: customEmojisKey(groupId),
      queryFn: () => getCustomEmojis(groupId!),
      enabled: !!groupId && engaged,
      staleTime: 5 * 60 * 1000,
    });
    const allEmojis: CustomEmoji[] = emojiData?.items ?? [];
    const emojisByShortcode = useMemo(() => {
      const m = new Map<string, CustomEmoji>();
      for (const e of allEmojis) m.set(e.shortcode, e);
      return m;
    }, [allEmojis]);

    const emojiSuggestions: EmojiSuggestion[] = useMemo(() => {
      if (emojiQuery === null) return [];
      const q = emojiQuery.toLowerCase();
      const standard = searchStandardEmojis(q, 20);
      const custom = q
        ? allEmojis.filter((e) => e.shortcode.toLowerCase().includes(q)).slice(0, 20)
        : allEmojis.slice(0, 20);
      return [
        ...standard.map((emoji): EmojiSuggestion => ({ kind: "standard", emoji })),
        ...custom.map((emoji): EmojiSuggestion => ({ kind: "custom", emoji })),
      ];
    }, [emojiQuery, allEmojis]);

    // Imperative handle
    useImperativeHandle(ref, () => ({
      getRaw: () => {
        const el = editorRef.current;
        if (!el) return value;
        return serializeEditorToRaw(el);
      },
      clearMentions: () => {
        // No-op: with contenteditable, mention state is in the DOM and clears
        // when the editor is reset to "".
      },
      focus: () => editorRef.current?.focus(),
      blur: () => editorRef.current?.blur(),
      prefillMention: ({ userId, displayName }) => {
        const el = editorRef.current;
        if (!el || getTextRepresentation(el).trim() !== "") return;
        const { fragment, text } = mentionFragment(userId, displayName);
        el.innerHTML = "";
        el.appendChild(fragment);
        setCaretOffsetInText(el, text.length);
        // The chip is content, and `value` will not say so (see below), so the
        // placeholder is told here — OSO-670, and `setEmptyFlag`'s header.
        setEmptyFlag(el, text);
        /**
         * IT DOES NOT CALL `onChange`, AND THAT IS THE WHOLE POINT.
         *
         * `onChange` is the caller's draft setter on the uncontrolled path, so
         * emitting here saved the chip's PLAIN TEXT — "@Bo Bendtsen ", with no
         * `data-user-id` anywhere — to localStorage the instant the box
         * opened. Re-open it and the draft hydrated as inert text, this method
         * refused because the box was no longer empty, and the reply went out
         * naming somebody it did not tag: no `post_mentions` row, no
         * notification, the opposite of what the prefill is for. It also left
         * a stored draft behind every Svar that was never written, and made
         * Send live on a box the author had not typed in.
         *
         * `lastEmitted` is deliberately left at the empty string it already
         * holds, for the same reason: it must keep MATCHING `value`, or the
         * effect below — which re-runs whenever the group's custom emoji land,
         * asynchronously, some time after mount — sees them differ and renders
         * `value` back into the editor, flattening the chip.
         *
         * Nothing is persisted until the author types, and their first
         * keystroke goes through `handleInput`, which emits the whole text,
         * chip included.
         */
      },
    }));

    // Initial value & external resets
    useEffect(() => {
      const el = editorRef.current;
      if (!el) return;
      // Re-render content from value when it differs from what we emitted.
      // This handles parent-driven resets (e.g. after submit) and initial mount.
      if (value !== lastEmitted.current) {
        renderTextIntoEditor(el, value, emojisByShortcode);
        lastEmitted.current = value;
        setEmptyFlag(el, value);
        // A parent-driven CLEAR (the post-submit reset) also closes the
        // internally-held row, so a composer that revealed it via a shortcut
        // (OSO-647: the icon-less ones have no other close) starts compact
        // again for the next message. Internal state only — a parent driving
        // the row through formattingOpen closes it in its own reset.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        if (value === "") setInternalToolbarOpen(false);
      }
    }, [value, emojisByShortcode]);

    // The leading-text highlight. Re-applied after every change, because the
    // browser may have typed into (or out of) the span; the caret is put
    // back only when the DOM actually moved, and only if it was in here.
    const composingRef = useRef(false);
    const highlightPendingRef = useRef(false);
    const highlightLeadingRef = useRef(highlightLeading);
    useEffect(() => {
      highlightLeadingRef.current = highlightLeading;
    }, [highlightLeading]);
    const syncLeadingHighlight = useCallback(() => {
      const el = editorRef.current;
      if (!el) return;
      if (composingRef.current) {
        highlightPendingRef.current = true;
        return;
      }
      highlightPendingRef.current = false;
      const sel = window.getSelection();
      const hadCaret = !!sel && sel.rangeCount > 0 && el.contains(sel.anchorNode);
      const offset = hadCaret ? getCaretOffsetInText(el) : 0;
      if (applyLeadingHighlight(el, highlightLeadingRef.current ?? null) && hadCaret) {
        setCaretOffsetInText(el, offset);
      }
    }, []);
    useEffect(() => {
      syncLeadingHighlight();
    }, [highlightLeading, value, syncLeadingHighlight]);

    // autoFocus
    // This used to be one line inside the effect above, and therefore ran only
    // when the incoming value DIFFERED from what the editor last emitted. The
    // common case is the opposite: a composer opening on an empty draft has
    // `value === ""` and `lastEmitted === ""`, so the body was skipped and
    // `autoFocus` did nothing at all — the modal opened with the caret still
    // behind it, on whatever the trigger was (OSO-402).
    //
    // ON EVERY FLIP TO TRUE, not once per instance. A re-render with
    // `autoFocus` unchanged must not grab the caret back from wherever the
    // author moved it — that is what the previous value is compared against —
    // but a caller that turns `autoFocus` OFF and ON again means it twice.
    // `PostComposer` is exactly that caller: it passes `autoFocus={!showTitle}`
    // and the "Titel" button toggles `showTitle`. Turning the title field on
    // hands focus to it; turning it off UNMOUNTS the focused field, dropping
    // `document.activeElement` to `<body>`, and with a once-per-instance latch
    // the body editor never took it back — typing went nowhere until the
    // author clicked or Tabbed. That is OSO-402's own symptom.
    const hadAutoFocus = useRef(false);
    useEffect(() => {
      if (autoFocus && !hadAutoFocus.current) editorRef.current?.focus();
      hadAutoFocus.current = !!autoFocus;
    }, [autoFocus]);

    // Native paste listener for files
    // Attached directly to the contentEditable element to bypass React's
    // synthetic clipboard handling, which does not reliably fire for file
    // pastes on a contentEditable. stopImmediatePropagation prevents React's
    // onPaste from also running for file pastes.
    useEffect(() => {
      const el = editorRef.current;
      if (!el || !onPasteFiles) return;
      const handler = (e: globalThis.ClipboardEvent) => {
        const dt = e.clipboardData;
        if (!dt) return;
        const files: File[] = [];
        if (dt.files && dt.files.length > 0) {
          files.push(...Array.from(dt.files));
        } else if (dt.items) {
          for (const it of Array.from(dt.items)) {
            if (it.kind === "file") {
              const f = it.getAsFile();
              if (f) files.push(f);
            }
          }
        }
        if (files.length > 0) {
          e.preventDefault();
          e.stopImmediatePropagation();
          onPasteFiles(files);
        }
      };
      el.addEventListener("paste", handler);
      return () => el.removeEventListener("paste", handler);
    }, [onPasteFiles]);

    // Native drop listener for files
    // Without this, a dragged-in image falls through to the browser's
    // default contentEditable drop behavior, which inserts it as an inline
    // <img> (a data: URL) rather than routing it through onPasteFiles like a
    // pasted file — landing as an inline image instead of an attachment
    // (OSO-359). Same reasoning as the paste listener above: native, not
    // React's synthetic handling, so it is attached directly to the element.
    useEffect(() => {
      const el = editorRef.current;
      if (!el || !onPasteFiles) return;
      const handleDragOver = (e: globalThis.DragEvent) => {
        e.preventDefault();
      };
      const handleDrop = (e: globalThis.DragEvent) => {
        const dt = e.dataTransfer;
        if (!dt) return;
        const files = Array.from(dt.files ?? []);
        if (files.length > 0) {
          e.preventDefault();
          e.stopImmediatePropagation();
          onPasteFiles(files);
        }
      };
      el.addEventListener("dragover", handleDragOver);
      el.addEventListener("drop", handleDrop);
      return () => {
        el.removeEventListener("dragover", handleDragOver);
        el.removeEventListener("drop", handleDrop);
      };
    }, [onPasteFiles]);

    // Trigger detection
    const checkTriggers = useCallback(() => {
      const el = editorRef.current;
      if (!el) return;
      const text = getTextRepresentation(el);
      const cursor = getCaretOffsetInText(el);

      // Mention
      const m = extractMentionQuery(text, cursor);
      if (m) {
        if (m.query !== prevMentionQuery.current) {
          setMentionIndex(0);
          prevMentionQuery.current = m.query;
        }
        setMentionQuery(m.query);
        setMentionStart(m.startIndex);
        setEmojiQuery(null);
        setEmojiStart(-1);
        prevEmojiQuery.current = null;
        return;
      }
      setMentionQuery("");
      setMentionStart(-1);
      prevMentionQuery.current = "";

      // Emoji
      const e = extractEmojiQuery(text, cursor);
      if (e) {
        if (e.query !== prevEmojiQuery.current) {
          setEmojiIndex(0);
          prevEmojiQuery.current = e.query;
        }
        setEmojiQuery(e.query);
        setEmojiStart(e.startIndex);
        return;
      }
      setEmojiQuery(null);
      setEmojiStart(-1);
      prevEmojiQuery.current = null;
    }, []);

    // Handlers
    const handleInput = useCallback(() => {
      const el = editorRef.current;
      if (!el) return;
      const text = getTextRepresentation(el);
      lastEmitted.current = text;
      setEmptyFlag(el, text);
      onChange(text);
      checkTriggers();
      // The box has just grown by whatever was typed into it — see
      // lib/caretInView.ts for why the browser's own scrolling is not enough.
      // Last, and before React re-renders: the mention/emoji dropdowns measure
      // the caret in a layout effect, so they anchor against the final scroll
      // position rather than the one from before this keystroke.
      scrollCaretIntoView(el);
    }, [onChange, checkTriggers]);

    // Recompute which of strong/em/u the current selection sits fully inside,
    // for the toolbar's pressed-button state. Only while the toolbar is open —
    // this runs on every selection change (key up / mouse up), so skipping it
    // otherwise avoids doing that work on every keystroke of normal typing.
    const refreshActiveFormats = useCallback(() => {
      if (!toolbarOpen) return;
      const el = editorRef.current;
      const sel = window.getSelection();
      if (!el || !sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) {
        setActiveFormats(new Set());
        return;
      }
      const range = sel.getRangeAt(0);
      const next = new Set<InlineTag>();
      (["strong", "em", "u"] as const).forEach((tag) => {
        if (isRangeFullyWrapped(range, tag)) next.add(tag);
      });
      setActiveFormats(next);
    }, [toolbarOpen]);

    // The pressed states, computed on the render where the row OPENS —
    // whichever path opened it: a shortcut, the Aa icon, or a parent's own
    // trigger (PostComposer/PostEditor — a prop change no handler in here
    // sees, which is why this is an effect and not part of setToolbarOpen).
    // The guard above keeps them frozen the whole time the row is closed, so
    // without this the row would come up showing the state from when it was
    // last open — B unpressed over bold text (OSO-623 review). The state it
    // sets is derived from the live DOM selection, unreadable during render;
    // it runs only on the closed→open transition, so no cascade.
    useEffect(() => {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (toolbarOpen) refreshActiveFormats();
    }, [toolbarOpen, refreshActiveFormats]);

    // Toggle strong/em/u formatting around the current selection (or, if the
    // caret is collapsed, open an empty tag to type into). See toggleInlineTag
    // below for the DOM mechanics — this is real WYSIWYG formatting, not
    // literal markdown characters typed into the text. Returns whether it
    // actually toggled anything — false when there is no usable selection.
    const applyInlineToggle = useCallback(
      (tag: InlineTag): boolean => {
        const el = editorRef.current;
        if (!el) return false;
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return false;
        const range = sel.getRangeAt(0);
        if (!el.contains(range.commonAncestorContainer)) return false;
        toggleInlineTag(el, range, tag);
        handleInput();
        refreshActiveFormats();
        return true;
      },
      [handleInput, refreshActiveFormats]
    );

    const handleMentionSelect = useCallback(
      (sel: MentionSelection) => {
        const el = editorRef.current;
        if (!el || mentionStart < 0) return;
        const cursor = getCaretOffsetInText(el);
        // Replace [mentionStart, cursor) with the chip + its trailing space.
        // `@[id]` (or `@[alle]`) is what `getRaw()` serializes the chip to —
        // an ID, never a handle (O-21/O-22). See `lib/richText/editorDom.ts`.
        const chip =
          sel.kind === "all"
            ? mentionEveryoneFragment()
            : mentionFragment(sel.userId, sel.displayName);
        replaceTextRangeWithFragment(el, mentionStart, cursor, chip.fragment);

        setCaretOffsetInText(el, mentionStart + chip.text.length);

        setMentionQuery("");
        setMentionStart(-1);
        prevMentionQuery.current = "";

        // Recompute and emit
        const text = getTextRepresentation(el);
        lastEmitted.current = text;
        setEmptyFlag(el, text);
        onChange(text);
      },
      [mentionStart, onChange]
    );

    const handleEmojiSelect = useCallback(
      (suggestion: EmojiSuggestion) => {
        const el = editorRef.current;
        if (!el || emojiStart < 0) return;
        const cursor = getCaretOffsetInText(el);

        const fragment = document.createDocumentFragment();
        let insertedLen = 0;

        if (suggestion.kind === "standard") {
          fragment.appendChild(document.createTextNode(suggestion.emoji.char));
          insertedLen = suggestion.emoji.char.length;
        } else {
          const img = document.createElement("img");
          img.src = suggestion.emoji.url;
          img.alt = `:${suggestion.emoji.shortcode}:`;
          img.dataset.shortcode = suggestion.emoji.shortcode;
          img.className = "theta-inline-emoji";
          img.contentEditable = "false";
          fragment.appendChild(img);
          insertedLen = `:${suggestion.emoji.shortcode}:`.length;
        }
        // Trailing space (only if next char isn't already whitespace)
        const text = getTextRepresentation(el);
        const nextChar = text[cursor] ?? "";
        if (nextChar !== " " && nextChar !== "\n") {
          fragment.appendChild(document.createTextNode(" "));
          insertedLen += 1;
        }

        replaceTextRangeWithFragment(el, emojiStart, cursor, fragment);

        setCaretOffsetInText(el, emojiStart + insertedLen);

        setEmojiQuery(null);
        setEmojiStart(-1);
        prevEmojiQuery.current = null;

        const newText = getTextRepresentation(el);
        lastEmitted.current = newText;
        setEmptyFlag(el, newText);
        onChange(newText);
      },
      [emojiStart, onChange]
    );

    const mentionActive = mentionQuery.length > 0 && mentionRows.length > 0;
    const emojiActive = emojiQuery !== null && emojiSuggestions.length > 0;

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLDivElement>) => {
        // While an IME candidate window is open (Chinese/Japanese/Korean input),
        // Enter confirms the candidate — it must not reach the submit, mention
        // or emoji handlers. keyCode 229 is the Safari/older-browser fallback.
        if (e.nativeEvent.isComposing || e.keyCode === 229) return;

        // Formatting shortcuts: Cmd/Ctrl+B/I/U for bold/italic/underline. None
        // of these keys are used by the mention or emoji autocomplete (which
        // only reacts to Escape/Enter/Tab/arrows), so this is safe to check
        // unconditionally, dropdown open or not.
        if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey) {
          const key = e.key.toLowerCase();
          const tag: InlineTag | null =
            key === "b" ? "strong" : key === "i" ? "em" : key === "u" ? "u" : null;
          if (tag) {
            e.preventDefault();
            // Edge-triggered: a held key auto-repeats keydown, and re-running
            // the toggle every repeat makes the final state a coin flip on how
            // long the key was down (each run also writes the draft).
            if (e.repeat) return;
            // A shortcut also REVEALS the B/I/U row (OSO-623): its pressed
            // buttons are the only feedback that the toggle took — most of all
            // at a collapsed caret, where nothing visible changes until you
            // type. Goes through setToolbarOpen so a parent driving the row
            // from its own "Aa" button (formattingOpen) hears about it too.
            // In the minimal composers (inline comments, DMs, in-place edits,
            // all hideFormattingIcon with no parent trigger) the reveal is the
            // ONLY way the row ever shows (OSO-647) — there it closes again
            // when the parent clears the field after submit, see the external-
            // reset effect. Only when the toggle actually took — a keystroke
            // with no usable selection reveals nothing.
            if (applyInlineToggle(tag)) setToolbarOpen(true);
            return;
          }
        }

        if (mentionActive) {
          if (e.key === "Escape") {
            e.preventDefault();
            setMentionQuery("");
            setMentionStart(-1);
            prevMentionQuery.current = "";
            return;
          }
          if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            const row = mentionRows[mentionIndex] ?? mentionRows[0];
            if (row) handleMentionSelect(row);
            return;
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setMentionIndex((i) => Math.min(i + 1, mentionRows.length - 1));
            return;
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setMentionIndex((i) => Math.max(i - 1, 0));
            return;
          }
        }
        if (emojiActive) {
          if (e.key === "Escape") {
            e.preventDefault();
            setEmojiQuery(null);
            setEmojiStart(-1);
            prevEmojiQuery.current = null;
            return;
          }
          if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            const em = emojiSuggestions[emojiIndex] ?? emojiSuggestions[0];
            if (em) handleEmojiSelect(em);
            return;
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setEmojiIndex((i) => Math.min(i + 1, emojiSuggestions.length - 1));
            return;
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setEmojiIndex((i) => Math.max(i - 1, 0));
            return;
          }
        }
        // Enter behaviour (uniform across every composer): Enter inserts a
        // newline, Cmd/Ctrl+Enter submits. Enter-to-submit kept catching people
        // out mid-thought, and a half-finished comment is public and permanent
        // while an extra keystroke costs nothing.
        if (e.key === "Enter") {
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            if (onSubmit) {
              recordSubmitShortcutUse();
              onSubmit();
            }
            return;
          }
          // Insert a single <br> manually so browsers don't wrap text in <div>/<p>.
          e.preventDefault();
          insertBrAtCaret();
          handleInput();
          return;
        }
        if (e.key === "Escape" && onEscape) {
          // MARKED HANDLED, like the two suggestion dismissals above. This
          // one is a React-root listener, so it runs BEFORE the document-level
          // surfaces — without the flag, the Escape that cancelled a reply in
          // a lightbox's comment sidebar also tore the lightbox down.
          e.preventDefault();
          onEscape();
        }
      },
      [
        mentionActive,
        mentionRows,
        mentionIndex,
        handleMentionSelect,
        emojiActive,
        emojiSuggestions,
        emojiIndex,
        handleEmojiSelect,
        onSubmit,
        onEscape,
        handleInput,
        applyInlineToggle,
        setToolbarOpen,
      ]
    );

    const handlePaste = useCallback(
      (e: ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        // Insert as plain text at caret
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        range.deleteContents();
        // Split on newlines and insert <br>s
        const parts = text.split("\n");
        const fragment = document.createDocumentFragment();
        parts.forEach((part, i) => {
          if (i > 0) fragment.appendChild(document.createElement("br"));
          if (part) fragment.appendChild(document.createTextNode(part));
        });
        range.insertNode(fragment);
        // Place caret at end of inserted content
        sel.collapseToEnd();
        handleInput();
      },
      [handleInput]
    );

    // Empty-state placeholder via attribute (CSS handles the rest). This is
    // the FIRST-PAINT value only; from the first change on, `setEmptyFlag`
    // owns the attribute — see its header for why `value` cannot.
    const isEmpty = !value || value.length === 0;

    /**
     * The formatting row is the only thing that overlaps the editor, so it is
     * also the only reason to move the focus ring (OSO-671, on the editor's
     * className below).
     */
    const formattingRowShown = !hideFormattingIcon || toolbarOpen;

    return (
      <div className="relative">
        {formattingRowShown && (
          /**
           * IT STAYS PUT WHILE THE COMPOSER SCROLLS (OSO-593). The row sits
           * above the editor, so on a long post it scrolled off the top and
           * B/I/U went out of reach. `sticky top-0` pins it to whichever
           * scrollport it is in — for the post composer and editor that is
           * the dialog's own `overflow-y-auto` body. Sticky only holds while
           * its containing block is in view, so the short inline comment
           * composers are unaffected. `pb-1` rather than `mb-1` puts the same
           * 4px gap INSIDE the opaque box, so text scrolling under the row
           * does not surface in it.
           */
          <div className="sticky top-0 z-10 flex items-center gap-0.5 pb-1 bg-surface">
            {!hideFormattingIcon && (
              <button
                type="button"
                aria-label="Formatering"
                title="Tekstformatering (fed, kursiv, lister m.m.)"
                aria-pressed={toolbarOpen}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setToolbarOpen((o) => !o)}
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                  toolbarOpen
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                Aa
              </button>
            )}
            {toolbarOpen && (
              <>
                <button
                  type="button"
                  aria-label="Fed"
                  title="Fed (Ctrl+B)"
                  aria-pressed={activeFormats.has("strong")}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyInlineToggle("strong")}
                  className={`w-6 h-6 rounded-full flex items-center justify-center font-bold transition-colors ${
                    activeFormats.has("strong")
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  B
                </button>
                <button
                  type="button"
                  aria-label="Kursiv"
                  title="Kursiv (Ctrl+I)"
                  aria-pressed={activeFormats.has("em")}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyInlineToggle("em")}
                  className={`w-6 h-6 rounded-full flex items-center justify-center italic transition-colors ${
                    activeFormats.has("em")
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  I
                </button>
                <button
                  type="button"
                  aria-label="Understreget"
                  title="Understreget (Ctrl+U)"
                  aria-pressed={activeFormats.has("u")}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyInlineToggle("u")}
                  className={`w-6 h-6 rounded-full flex items-center justify-center underline transition-colors ${
                    activeFormats.has("u")
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  U
                </button>
              </>
            )}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-label={ariaLabel}
          aria-multiline={multiline ? "true" : "false"}
          data-placeholder={placeholder ?? ""}
          data-empty={isEmpty ? "true" : "false"}
          /**
           * `ring-inset` — THE FORMATTING ROW MUST NOT CLIP THE FOCUS RING
           * (OSO-671). Callers put the pill AND its `focus:ring-2` here, and a
           * Tailwind ring is a box-shadow drawn 2px OUTSIDE the border box —
           * the 2px the row above already owns, opaque and `z-10` on purpose
           * (see the row). So the ring's top arc vanished and the pill looked
           * broken. The ring belongs to the pill, so it moves inside it.
           *
           * ONLY WHILE THE ROW IS UP, because only the row clips: the bordered
           * callers (CommentEditor, MessageBubble) keep the outer ring they
           * have always had for the whole time their row is closed, which is
           * almost always. An inset ring is 2px of an already-small `py-1.5`,
           * and taking it from a composer nothing overlaps buys nothing.
           */
          className={`theta-rich-text-input ${formattingRowShown ? "ring-inset" : ""} ${className ?? ""}`}
          style={minHeight ? { minHeight } : undefined}
          onInput={handleInput}
          onCompositionStart={() => {
            composingRef.current = true;
          }}
          onCompositionEnd={() => {
            composingRef.current = false;
            if (highlightPendingRef.current) syncLeadingHighlight();
          }}
          onFocus={() => setEngaged(true)}
          onKeyDown={handleKeyDown}
          onKeyUp={() => {
            checkTriggers();
            refreshActiveFormats();
          }}
          onMouseUp={() => {
            checkTriggers();
            refreshActiveFormats();
          }}
          onPaste={handlePaste}
          onBlur={() => {
            // Hide dropdowns on blur (delayed so click on dropdown works)
            setTimeout(() => {
              if (document.activeElement !== editorRef.current) {
                setMentionQuery("");
                setMentionStart(-1);
                setEmojiQuery(null);
                setEmojiStart(-1);
              }
            }, 150);
          }}
        />
        {mentionActive && (
          <MentionDropdown
            users={mentionUsers}
            allRank={mentionAllRank}
            selectedIndex={mentionIndex}
            onSelect={handleMentionSelect}
            anchorRef={editorRef}
          />
        )}
        {emojiActive && (
          <EmojiSuggestionDropdown
            suggestions={emojiSuggestions}
            selectedIndex={emojiIndex}
            onSelect={handleEmojiSelect}
            anchorRef={editorRef}
          />
        )}
      </div>
    );
  }
);
